import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  AccessTime,
  Person,
  Delete,
  PlayArrow,
  PersonAdd,
  CalendarMonth,
  MailOutline,
  Send,
  Error as ErrorIcon,
  Schedule,
} from "@mui/icons-material";
import { Link as RouterLink, useParams, useHistory } from "react-router-dom";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import PreviewOutlinedIcon from "@mui/icons-material/PreviewOutlined";
import dayjs from "dayjs";

import { useEventDetails } from "src/queries/useEventDetails";
import { useFetchAllEventGuests } from "src/queries/useFetchAllEventGuests";
import Loading from "src/componentsV2/Loading";
import ErrorPage from "src/pages/ErrorPage";
import { GuestStatusIndicator } from "src/componentsV2/GuestStatusIndicator";
import { SelectTable } from "src/componentsV2/tables/SelectTable";
import { useWebinarGuestCount } from "src/queries/useWebinarGuestCount";
import {
  useAddEventGuest,
  useUpdateMeetings,
  useDeleteWebinars,
  useAssignFollowUpTemplate,
  useBeginFollowUp,
  useScheduleFollowUp,
  useAssignHost,
} from "src/mutations";
import { useSnackbar, useConfirmationDialog } from "src/hooks";
import {
  Contact,
  GuestStatus,
  Guest,
  AssignedHost,
  meetingStatusOptionFromValue,
  FollowUpStatus,
} from "src/types";
import { toInstanceStatusColor } from "src/meetings/props";
import { formatMeetingDuration } from "src/utils/formatMeetingDuration";
import { mutate } from "swr";
import { AddEventGuestDialog } from "src/componentsV2/dialogs/AddEventGuestDialog";
import { FollowUpButton } from "src/componentsV2/FollowUpButton";
import { FollowUpTemplateDropdown } from "src/componentsV2/FollowUpTemplateDropdown";
import ResendPromotionModal from "src/componentsV2/dialogs/ResendPromotionModal";
import { CreateTemplateModal } from "src/componentsV2/dialogs/CreateTemplateModal";
import { HostSelectInput } from "src/componentsV2/inputs";
import { SkinnyMeetingDefinition } from "src/queries";
import { INVITE_STYLE } from "../../../meetingTypes/invite/props";
import { FollowUpStatusCell } from "src/componentsV2/FollowUpStatusCell";
import { WebinarEmailHistoryModal } from "src/componentsV2/dialogs/WebinarEmailHistoryModal";
import { Pages } from "src/componentsV2/Pagination";
import PrimaryButton from "src/componentsV2/buttons/PrimaryButton";

type EventDetailsPageTabs =
  | "Everyone"
  | "Accepted"
  | "Declined"
  | "No Response"
  | "Awaiting Response"
  | "No Follow Up Template"
  | "Needs Host"
  | "Follow Up Not Started";

// Shared button styles
const BUTTON_STYLES = {
  backgroundColor: "#6b7280",
  color: "white",
  textTransform: "none" as const,
  "&:hover": {
    backgroundColor: "#4b5563",
  },
  "&:disabled": {
    backgroundColor: "#e0e0e0",
    color: "#9e9e9e",
  },
};

const BLOCKED_FOLLOW_UP_STATUSES: ReadonlySet<FollowUpStatus> = new Set([
  "initiated",
]);

function EventDetails() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [openSnackbar] = useSnackbar();
  const [tab, setTab] = useState<EventDetailsPageTabs>("Everyone");
  const [followUpStatusFilter, setFollowUpStatusFilter] = useState<string>("");
  const [guestStatusFilter, setGuestStatusFilter] = useState<GuestStatus | "">(
    "",
  );
  const [openResendModal, setOpenResendModal] = useState(false);
  const [resendGuests, setResendGuests] = useState<Guest[]>([]);
  const [loadingResendGuests, setLoadingResendGuests] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const openConfirmationDialog = useConfirmationDialog();

  const guestCounts = useWebinarGuestCount(id);

  const { data, loading, error } = useEventDetails(id, {
    guestStatusFilter,
    followUpStatusFilter,
    paging: {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    },
  });

  // Reset page when filters or page size change
  useEffect(() => {
    setPage(1);
  }, [guestStatusFilter, followUpStatusFilter, pageSize]);

  // Resend is only available when event is in scheduling status and has staging guests
  // The actual eligible guests are fetched when opening the modal
  const isResendDisabled =
    !data ||
    data.status !== "scheduling" ||
    (guestCounts.data?.stagingTotal ?? 0) === 0;

  const [showAddGuestDialog, setShowAddGuestDialog] = useState(false);
  const [selectedGuestIds, setSelectedGuestIds] = useState<(number | string)[]>(
    [],
  );
  const [editingTemplateGuestId, setEditingTemplateGuestId] = useState<
    number | null
  >(null);
  const [showCreateTemplateModal, setShowCreateTemplateModal] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [editingHostGuestId, setEditingHostGuestId] = useState<number | null>(
    null,
  );
  const [emailHistoryGuest, setEmailHistoryGuest] = useState<Guest | null>(
    null,
  );

  /** Returns true if the guest can be edited/selected for follow-up actions. */
  const guestStatusAllowedForFollowUp = (guest: Guest) => {
    return !BLOCKED_FOLLOW_UP_STATUSES.has(
      guest.followUpStatus ?? ("" as FollowUpStatus),
    );
  };

  // Auto-deselect guests with blocked statuses when page data changes
  useEffect(() => {
    if (!data?.guests) return;

    setSelectedGuestIds((prevSelected) => {
      const toUnselect = data
        .guests!.filter((guest) => !guestStatusAllowedForFollowUp(guest))
        .map((guest) => guest.id);

      if (toUnselect.length === 0) return prevSelected;

      const set = new Set(prevSelected);
      toUnselect.forEach((id) => set.delete(id));
      return Array.from(set);
    });
  }, [data?.guests]);

  const updateMeetings = useUpdateMeetings();
  const deleteWebinar = useDeleteWebinars();
  const addEventGuest = useAddEventGuest();
  const assignFollowUpTemplate = useAssignFollowUpTemplate();
  const beginFollowUp = useBeginFollowUp();
  const scheduleFollowUp = useScheduleFollowUp();
  const assignHost = useAssignHost();
  const fetchAllEventGuests = useFetchAllEventGuests();

  const handleTemplateAssignment = useCallback(
    async (guestId: number, template: SkinnyMeetingDefinition | null) => {
      if (!template) return;

      try {
        const result = await assignFollowUpTemplate({
          eventId: Number.parseInt(id),
          templateId: template.id,
          contactIds: [guestId],
        });

        // Check if there are conflicts in the response
        if (result.conflicts && result.conflicts.length > 0) {
          // Template was assigned but there are host conflicts
          openSnackbar("Template assigned - host assignment needed");
        } else {
          openSnackbar("Template assigned successfully");
        }
        setEditingTemplateGuestId(null);
      } catch (error) {
        openSnackbar("Failed to assign template");
      }
    },
    [assignFollowUpTemplate, openSnackbar, id],
  );

  const handleCreateNewTemplate = () => {
    setShowCreateTemplateModal(true);
  };

  const handleTemplateCreated = useCallback(
    (newTemplate: SkinnyMeetingDefinition) => {
      setShowCreateTemplateModal(false);
      setRefreshTrigger((prev) => prev + 1); // Trigger dropdown refresh

      // If we were editing a guest, assign the new template to them
      if (editingTemplateGuestId !== null) {
        // Small delay to ensure the template is fully saved before proceeding
        setTimeout(() => {
          handleTemplateAssignment(editingTemplateGuestId, newTemplate);
        }, 100);
      } else {
        setEditingTemplateGuestId(null);
      }
    },
    [editingTemplateGuestId, handleTemplateAssignment],
  );

  // Handle escape key to cancel editing
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (editingTemplateGuestId !== null) {
          setEditingTemplateGuestId(null);
        }
        if (editingHostGuestId !== null) {
          setEditingHostGuestId(null);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [editingTemplateGuestId, editingHostGuestId]);

  const handleAddEventGuest = async (contact: Contact) => {
    try {
      setShowAddGuestDialog(false);
      await addEventGuest(Number.parseInt(id), contact.id);
      openSnackbar("Event guest successfully added");
    } catch (error: any) {
      const errorMsg =
        error?.response?.body?.message || "Error adding event guest";
      openSnackbar(errorMsg);
    }
  };

  const activateWebinar = useCallback(async () => {
    const { failures } = await updateMeetings([
      {
        enabled: true,
        id: Number.parseInt(id),
      },
    ]);

    if (failures?.length > 0) {
      openSnackbar("Failed to activate event");
      return;
    }

    await mutate([`/api/webinars/${id}`, { guestStatusFilter }]);

    openSnackbar("Event in Progress");
  }, [id, openSnackbar, updateMeetings, guestStatusFilter]);

  const isActivateDisabled =
    !data || data.status !== "initialized" || data.active;

  const isAddGuestDisabled =
    !data || !["initialized", "scheduling"].includes(data.status);

  const handleDeleteWebinar = () => {
    openConfirmationDialog(
      "Delete Confirmation",
      "Are you sure you want to delete this webinar event?",
      async () => {
        try {
          await deleteWebinar(Number.parseInt(id));
          history.push("/events");
          openSnackbar("Webinar successfully deleted");
        } catch (error) {
          openSnackbar("Failed to delete webinar");
        }
      },
    );
  };

  const isMeetingLink = (s: string) => /^(https?:\/\/|www\.)/.test(s);

  const handleGuestSelection = (checked: boolean, guests: Guest[]) => {
    if (checked) {
      // Remove guests from selection
      setSelectedGuestIds((prev) =>
        prev.filter((id) => !guests.some((guest) => guest.id === id)),
      );
    } else {
      // Add guests to selection
      setSelectedGuestIds((prev) => [
        ...prev,
        ...guests.map((guest) => guest.id).filter((id) => !prev.includes(id)),
      ]);
    }
  };

  const handleTemplateAssigned = useCallback(() => {
    // Clear selection and refresh data after template assignment
    setSelectedGuestIds([]);
    mutate(`/api/webinars/${id}`);
  }, [id]);

  const handleBeginFollowUp = useCallback(
    async (selectedGuestIds?: number[]) => {
      try {
        const contactIds = selectedGuestIds ? selectedGuestIds : undefined;

        const result = await beginFollowUp({
          eventId: Number.parseInt(id),
          contactIds,
        });

        openSnackbar(`Follow-up initiated for ${result.initiated} guest(s)`);
      } catch (error) {
        openSnackbar("Failed to begin follow-up");
        console.error("Error beginning follow-up:", error);
      }
    },
    [beginFollowUp, id, openSnackbar],
  );

  const handleScheduleFollowUp = useCallback(
    async (delayHours: number, selectedGuestIds?: number[]) => {
      try {
        const contactIds = selectedGuestIds ? selectedGuestIds : undefined;

        const result = await scheduleFollowUp({
          eventId: Number.parseInt(id),
          contactIds,
          delayHours,
        });

        openSnackbar(
          `Follow-up scheduled for ${result.guestCount} guest(s) at ${new Date(result.scheduledTime).toLocaleString()}`,
        );
      } catch (error) {
        openSnackbar("Failed to schedule follow-up");
        console.error("Error scheduling follow-up:", error);
      }
    },
    [scheduleFollowUp, id, openSnackbar],
  );

  const handleAssignHost = useCallback(
    async (guestId: number, newHostId: number) => {
      try {
        await assignHost({
          eventId: Number.parseInt(id),
          contactId: guestId,
          hostId: newHostId,
        });

        setEditingHostGuestId(null);
        openSnackbar("Host assigned successfully");

        await mutate([`/api/webinars/${id}`, { guestStatusFilter }]);
      } catch (error) {
        openSnackbar("Failed to assign host");
        console.error("Error assigning host:", error);
      }
    },
    [id, openSnackbar, assignHost, guestStatusFilter],
  );

  const handleOpenResendModal = useCallback(async () => {
    setLoadingResendGuests(true);

    try {
      // Fetch ALL staging guests (no pagination)
      const response = await fetchAllEventGuests(id, {
        guestStatusFilter: "staging",
      });
      const allStagingGuests = (response.guests as Guest[]) || [];

      // Filter out guests with bad tokens (same logic as eligibleResendGuests)
      const eligible = allStagingGuests.filter((g) => {
        const hasBadToken =
          !!g.promotionSender &&
          g.promotionSender.tokenStatus &&
          g.promotionSender.tokenStatus !== "current";
        return !hasBadToken;
      });

      setResendGuests(eligible);
      setOpenResendModal(true);
    } catch (error) {
      console.error("Error fetching staging guests:", error);
      openSnackbar("Failed to load guests for resend");
    } finally {
      setLoadingResendGuests(false);
    }
  }, [fetchAllEventGuests, id, openSnackbar]);

  const getTemplateIcon = (inviteStyle: string) => {
    switch (inviteStyle) {
      case INVITE_STYLE.CALENDAR_FIRST:
        return <CalendarMonth sx={{ fontSize: 16, mr: 1 }} />;
      case INVITE_STYLE.CUSTOM_INVITE:
        return <MailOutline sx={{ fontSize: 16, mr: 1 }} />;
      default:
        return null;
    }
  };

  const hostLabel = (user: AssignedHost | null | undefined): string => {
    if (!user) {
      return "No Host Assigned";
    }

    const firstName = user.firstName?.trim() || "";
    const lastName = user.lastName?.trim() || "";
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) {
      return fullName;
    }

    const email = user.email?.trim() || "";
    if (email) {
      return email;
    }

    return "Unknown Host";
  };

  if (error) {
    return <ErrorPage status={error.status} />;
  }

  if (loading) {
    return <Loading />;
  }

  if (!data) {
    return <ErrorPage status={404} />;
  }

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ pb: 1 }}
      >
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link component={RouterLink} to="/events">
            Events
          </Link>
          <Typography component="span">{id}</Typography>
        </Breadcrumbs>
        <IconButton
          onClick={handleDeleteWebinar}
          title="Delete Webinar"
          sx={{ color: "text.primary" }}
        >
          <Delete />
        </IconButton>
      </Stack>

      <Stack spacing={2}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignContent="flex-start"
        >
          <Stack spacing={1}>
            <Stack direction="row" spacing={2} alignItems="center">
              {data?.meetingDefinition?.name ? (
                <Typography
                  variant="h4"
                  sx={{ color: "primary.dark", fontWeight: "bold" }}
                >
                  {data?.meetingDefinition?.name}
                </Typography>
              ) : (
                <Typography
                  variant="h4"
                  sx={{
                    color: "primary.dark",
                    fontWeight: "bold",
                    fontStyle: "italic",
                  }}
                >
                  [EVENT MISSING]
                </Typography>
              )}
              <Chip
                label={
                  meetingStatusOptionFromValue(data.status)?.label || "Unknown"
                }
                size="medium"
                sx={{
                  backgroundColor:
                    toInstanceStatusColor(data.status) || "#9e9e9e",
                  color: "#fff",
                  fontWeight: "bold",
                }}
              />
            </Stack>

            <Stack direction="row" spacing={2}>
              <Stack direction="row" spacing={1}>
                <Person />
                <Stack>
                  <Typography fontWeight="bold">Host</Typography>
                  {data.host ? (
                    <Stack>
                      <Typography>
                        {data.host.firstName} {data.host.lastName}
                      </Typography>
                      <Typography color="primary">{data.host.email}</Typography>
                    </Stack>
                  ) : (
                    <Typography>No Host</Typography>
                  )}
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1}>
                <AccessTime />
                <Stack>
                  <Typography fontWeight="bold">Meeting Time</Typography>
                  {data.startTime ? (
                    <Stack>
                      <Typography>
                        {dayjs(data.startTime).format("M/D/YYYY")}
                      </Typography>
                      <Typography>
                        {dayjs(data.startTime).format("h:mm A z")}
                      </Typography>
                    </Stack>
                  ) : (
                    <Typography>Not yet scheduled</Typography>
                  )}
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Schedule />
                <Stack>
                  <Typography fontWeight="bold">Meeting Duration</Typography>
                  <Typography>
                    {formatMeetingDuration(data.duration || 60)}
                  </Typography>
                </Stack>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Stack sx={{ width: "100%", maxWidth: "350px" }} spacing={1}>
                  <Typography fontWeight="bold">
                    Template Type: Kronologic Event
                  </Typography>
                  <Typography>
                    <Typography fontWeight="bold" component="span">
                      Event Location:
                    </Typography>{" "}
                    {/*TODO: maybe also use same check for non-webinar meeting details*/}
                    {data?.location ? (
                      isMeetingLink(data.location) ? (
                        <a
                          href={data.location}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Link to meeting
                        </a>
                      ) : (
                        data.location
                      )
                    ) : (
                      "None"
                    )}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Button
                onClick={() => setShowAddGuestDialog(true)}
                startIcon={<PersonAdd />}
                disabled={isAddGuestDisabled}
                variant="contained"
                sx={BUTTON_STYLES}
              >
                Add Guest
              </Button>

              <Button
                onClick={activateWebinar}
                disabled={isActivateDisabled}
                startIcon={<PlayArrow />}
                variant="contained"
                sx={BUTTON_STYLES}
              >
                Activate Event
              </Button>

              <Button
                onClick={handleOpenResendModal}
                disabled={isResendDisabled || loadingResendGuests}
                startIcon={<Send />}
                variant="contained"
                sx={BUTTON_STYLES}
              >
                {loadingResendGuests
                  ? "Loading..."
                  : `Resend Promotion Message (${data?.status === "scheduling" ? (guestCounts.data?.stagingTotal ?? 0) : 0})`}
              </Button>
            </Stack>
            <Grid container spacing={0}>
              <Grid item xs sx={{ minWidth: 0 }}>
                <Stack
                  sx={{
                    borderStyle: "solid",
                    borderRadius: "5px",
                    borderColor: "black",
                    borderWidth: "1px",
                    minWidth: "1150px",
                    minHeight: "600px",
                    pt: 2,
                    pb: 2,
                    pl: 0,
                    pr: 2,
                    gap: 2,
                    overflow: "hidden",
                    overflowX: "scroll",
                  }}
                >
                  <Tabs
                    value={tab}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                      textDecoration: 0,
                      borderBottom: "1px solid #CDD6DD",
                      minHeight: "48px",
                      "& .MuiTab-root": {
                        minWidth: "auto",
                        padding: "6px 16px",
                      },
                    }}
                    TabIndicatorProps={{
                      style: { backgroundColor: "transparent" },
                    }}
                    onChange={(_event, value: EventDetailsPageTabs) => {
                      switch (value) {
                        case "Everyone":
                          setFollowUpStatusFilter("");
                          setGuestStatusFilter("");
                          break;
                        case "Accepted":
                          setFollowUpStatusFilter("");
                          setGuestStatusFilter("accepted");
                          break;
                        case "Declined":
                          setFollowUpStatusFilter("");
                          setGuestStatusFilter("declined");
                          break;
                        case "No Response":
                          setFollowUpStatusFilter("");
                          setGuestStatusFilter("no_response");
                          break;
                        case "Awaiting Response":
                          setFollowUpStatusFilter("");
                          setGuestStatusFilter("waiting_for_first_response");
                          break;
                        case "No Follow Up Template":
                          setGuestStatusFilter("");
                          setFollowUpStatusFilter("needs_template");
                          break;
                        case "Needs Host":
                          setGuestStatusFilter("");
                          setFollowUpStatusFilter("needs_host");
                          break;
                        case "Follow Up Not Started":
                          setGuestStatusFilter("");
                          setFollowUpStatusFilter("!initiated");
                          break;
                      }
                      setTab(value);
                    }}
                  >
                    <Tab
                      label={`Everyone (${guestCounts.data?.total ?? 0})`}
                      value="Everyone"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`Accepted (${guestCounts.data?.acceptedTotal ?? 0})`}
                      value="Accepted"
                      sx={{ textTransform: "none" }}
                    />{" "}
                    <Tab
                      label={`Declined (${guestCounts.data?.declinedTotal ?? 0})`}
                      value="Declined"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`No Response (${guestCounts.data?.noResponseTotal ?? 0})`}
                      value="No Response"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`Awaiting Response (${guestCounts.data?.awaitingResponseTotal ?? 0})`}
                      value="Awaiting Response"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`No Follow Up Template (${guestCounts.data?.noFollowUpTemplateTotal ?? 0})`}
                      value="No Follow Up Template"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`Needs Host (${guestCounts.data?.needsHostTotal ?? 0})`}
                      value="Needs Host"
                      sx={{ textTransform: "none" }}
                    />
                    <Tab
                      label={`Follow Up Not Started (${guestCounts.data?.followUpNotInitiatedTotal ?? 0})`}
                      value="Follow Up Not Started"
                      sx={{ textTransform: "none" }}
                    />
                  </Tabs>
                  {data.guests && data.guests.length > 0 && (
                    <SelectTable
                      loading={loading}
                      data={data.guests}
                      selected={selectedGuestIds}
                      onSelect={handleGuestSelection}
                      getDisabled={(guest: Guest) =>
                        !!guest.followUpTemplate ||
                        !guestStatusAllowedForFollowUp(guest)
                      }
                      columns={[
                        {
                          id: "guestInfo",
                          label: "Guest",
                          component: (row: Guest) => (
                            <Stack>
                              <Typography variant="caption">
                                {row.firstName} {row.lastName}
                              </Typography>
                              <Typography variant="caption">
                                {row.email}
                              </Typography>
                            </Stack>
                          ),
                        },
                        {
                          id: "promotionSender",
                          label: "Promotion Sender",
                          component: (row: Guest) => {
                            const hasInvalidToken =
                              row.promotionSender &&
                              row.promotionSender.tokenStatus &&
                              row.promotionSender.tokenStatus !== "current";

                            const tooltipMessage =
                              row.promotionSender?.tokenStatus === "expired"
                                ? "Calendar Connection Token Expired"
                                : row.promotionSender?.tokenStatus ===
                                    "disabled"
                                  ? "Calendar Connection Disabled"
                                  : "";

                            return (
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="flex-start"
                              >
                                <Typography variant="caption">
                                  {row.promotionSender
                                    ? `${row.promotionSender.firstName} ${row.promotionSender.lastName}`
                                    : "Organizer"}
                                </Typography>
                                {hasInvalidToken && (
                                  <Tooltip title={tooltipMessage} arrow>
                                    <ErrorIcon
                                      sx={{
                                        color: "#d32f2f",
                                        fontSize: "16px",
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            );
                          },
                        },
                        {
                          id: "status",
                          label: "Event Guest Status",
                          component: (row: Guest) => (
                            <Box
                              sx={{
                                display: "inline-block",
                              }}
                            >
                              <GuestStatusIndicator status={row.status} />
                            </Box>
                          ),
                        },
                        {
                          id: "",
                          label: "",
                          component: (row: Guest) => (
                            <Box
                              sx={{
                                cursor: "pointer",
                                display: "flex",
                                justifyContent: "center",
                              }}
                              data-rh="Preview"
                              onClick={() => setEmailHistoryGuest(row)}
                            >
                              <PreviewOutlinedIcon />
                            </Box>
                          ),
                        },
                        {
                          id: "followUpTemplate",
                          label: "Follow Up Template",
                          width: 240,
                          component: (row: Guest) =>
                            guestStatusAllowedForFollowUp(row) ? (
                              <Stack sx={{ minWidth: 240, width: "100%" }}>
                                {editingTemplateGuestId === row.id ? (
                                  <FollowUpTemplateDropdown
                                    value={
                                      row.followUpTemplate as SkinnyMeetingDefinition
                                    }
                                    onChange={(template) =>
                                      handleTemplateAssignment(row.id, template)
                                    }
                                    onCreateNewTemplate={
                                      handleCreateNewTemplate
                                    }
                                    refreshTrigger={refreshTrigger}
                                  />
                                ) : (
                                  <Stack
                                    onClick={() =>
                                      setEditingTemplateGuestId(row.id)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setEditingTemplateGuestId(row.id);
                                      }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    sx={{
                                      cursor: "pointer",
                                      padding: 1.5,
                                      borderRadius: 1,
                                      minHeight: 40,
                                      width: "100%",
                                      "&:hover": {
                                        backgroundColor: "action.hover",
                                      },
                                    }}
                                  >
                                    {row.followUpTemplate ? (
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                      >
                                        {getTemplateIcon(
                                          row.followUpTemplate.inviteStyle,
                                        )}
                                        <Typography
                                          variant="caption"
                                          fontWeight="bold"
                                        >
                                          {row.followUpTemplate.name}
                                        </Typography>
                                      </Stack>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Click to assign template
                                      </Typography>
                                    )}
                                  </Stack>
                                )}
                              </Stack>
                            ) : (
                              <Stack
                                sx={{
                                  minWidth: 200,
                                  padding: 1.5,
                                  minHeight: 40,
                                }}
                              >
                                {row.followUpTemplate ? (
                                  <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                  >
                                    {row.followUpTemplate.name}
                                  </Typography>
                                ) : (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    None Assigned
                                  </Typography>
                                )}
                              </Stack>
                            ),
                        },
                        {
                          id: "assignedHost",
                          label: "Assigned Host",
                          component: (row: Guest) =>
                            guestStatusAllowedForFollowUp(row) ? (
                              <Stack sx={{ minWidth: 150 }}>
                                {editingHostGuestId === row.id ? (
                                  <HostSelectInput
                                    host={null}
                                    onHostSelect={(host) => {
                                      if (host) {
                                        handleAssignHost(row.id, host.id);
                                      } else {
                                        setEditingHostGuestId(null);
                                      }
                                    }}
                                    forMeetingTransfer={false}
                                  />
                                ) : (
                                  <Stack
                                    onClick={() => {
                                      if (row.followUpTemplate) {
                                        setEditingHostGuestId(row.id);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (
                                        row.followUpTemplate &&
                                        (e.key === "Enter" || e.key === " ")
                                      ) {
                                        e.preventDefault();
                                        setEditingHostGuestId(row.id);
                                      }
                                    }}
                                    role={
                                      row.followUpTemplate
                                        ? "button"
                                        : undefined
                                    }
                                    tabIndex={
                                      row.followUpTemplate ? 0 : undefined
                                    }
                                    sx={{
                                      cursor: row.followUpTemplate
                                        ? "pointer"
                                        : "default",
                                      padding: 1.5,
                                      borderRadius: row.followUpTemplate
                                        ? 1
                                        : 0,
                                      minHeight: 40,
                                      width: "100%",
                                      justifyContent: "flex-start",
                                      alignItems: "flex-start",
                                      "&:hover": row.followUpTemplate
                                        ? { backgroundColor: "action.hover" }
                                        : {},
                                      opacity: row.followUpTemplate
                                        ? 1
                                        : undefined, // Only set opacity if interactive
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      color={
                                        !row.followUpTemplate
                                          ? "text.secondary"
                                          : undefined
                                      }
                                    >
                                      {row.assignedHost
                                        ? hostLabel(row.assignedHost)
                                        : row.followUpTemplate
                                          ? "Click to assign host"
                                          : "None Assigned"}
                                    </Typography>
                                  </Stack>
                                )}
                              </Stack>
                            ) : (
                              <Stack
                                sx={{
                                  minWidth: 150,
                                  padding: 1.5,
                                  minHeight: 40,
                                  justifyContent: "flex-start",
                                  alignItems: "flex-start",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {row.assignedHost
                                    ? hostLabel(row.assignedHost)
                                    : "None Assigned"}
                                </Typography>
                              </Stack>
                            ),
                        },

                        {
                          id: "followUpStatus",
                          label: "Follow Up Status",
                          component: (row: Guest) => (
                            <FollowUpStatusCell
                              guest={row}
                              onClick={(meetingId) =>
                                history.push(`/meetings/${meetingId}`)
                              }
                            />
                          ),
                        },
                      ]}
                    />
                  )}
                  {/* Pagination */}
                  {data.guests && data.guests.length > 0 && (
                    <Box sx={{ p: 2 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Stack
                          sx={{
                            flexDirection: "row",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <Typography>Rows per page:</Typography>
                          <Select
                            variant="standard"
                            value={pageSize}
                            onChange={(event) =>
                              setPageSize(Number(event.target.value))
                            }
                          >
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                          </Select>
                        </Stack>

                        <Stack sx={{ flexDirection: "row", gap: "10px" }}>
                          <PrimaryButton
                            disabled={page <= 1}
                            onClick={() => setPage(Math.max(page - 1, 1))}
                          >
                            <ChevronLeft />
                          </PrimaryButton>
                          <Pages
                            pages={Math.ceil(
                              (data.guestsTotal || 0) / pageSize,
                            )}
                            currentPage={page}
                            onPageSelect={(newPage) => setPage(newPage)}
                          />
                          <PrimaryButton
                            disabled={
                              page >=
                              Math.ceil((data.guestsTotal || 0) / pageSize)
                            }
                            onClick={() =>
                              setPage(
                                Math.min(
                                  page + 1,
                                  Math.ceil((data.guestsTotal || 0) / pageSize),
                                ),
                              )
                            }
                          >
                            <ChevronRight />
                          </PrimaryButton>
                        </Stack>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Grid>
              <Grid
                item
                xs="auto"
                sx={{
                  width: 360,
                  flex: "0 0 360px",
                  ml: 2,
                  maxWidth: 360,
                  minWidth: 360,
                }}
              >
                <Stack spacing={2} sx={{ p: 2 }}>
                  <FollowUpButton
                    eventId={Number.parseInt(id)}
                    selectedGuestIds={selectedGuestIds.filter(
                      (id): id is number => typeof id === "number",
                    )}
                    onTemplateAssigned={handleTemplateAssigned}
                    onBeginFollowUp={handleBeginFollowUp}
                    onScheduleFollowUp={handleScheduleFollowUp}
                    eventStartTime={
                      data?.startTime ? new Date(data.startTime) : new Date()
                    }
                    eventEndTime={data?.endTime ? new Date(data.endTime) : null}
                    eventDuration={data?.duration || 60}
                    disabled={loading}
                    needsHostTotal={guestCounts.data?.needsHostTotal ?? 0}
                    readyForFollowUpTotal={
                      guestCounts.data?.readyForFollowUpTotal ?? 0
                    }
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Stack>

      <AddEventGuestDialog
        open={showAddGuestDialog}
        onClose={() => setShowAddGuestDialog(false)}
        onConfirm={handleAddEventGuest}
      />

      <ResendPromotionModal
        open={openResendModal}
        onClose={() => setOpenResendModal(false)}
        eventId={Number.parseInt(id)}
        guests={resendGuests}
      />

      <CreateTemplateModal
        open={showCreateTemplateModal}
        onClose={() => setShowCreateTemplateModal(false)}
        onTemplateCreated={handleTemplateCreated}
      />

      {emailHistoryGuest && (
        <WebinarEmailHistoryModal
          open={!!emailHistoryGuest}
          onClose={() => setEmailHistoryGuest(null)}
          meetingId={Number.parseInt(id)}
          guest={emailHistoryGuest}
        />
      )}
    </Container>
  );
}

export default EventDetails;
